import Icon from '@ant-design/icons';
import ShareAltOutlined from '@ant-design/icons/lib/icons/ShareAltOutlined';
import { Button, Card, FormInstance, Image, List, Statistic, TablePaginationConfig } from 'antd';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { RouteComponentProps } from 'react-router-dom';
import mediaApi from '../../apis/MediaApi';
import LayoutComponent from '../../components/LayoutComponent/LayoutComponent';
import CustomContext from '../../context/CustomContext';
import { LegacyPage } from '../../model/Elements';
import { Media } from '../../model/Entities';
import notificationService from '../../services/NotificationService';
import tableService from '../../services/TableService';
import { ReactComponent as shareSvg } from '../../resources/images/ico-share.svg';
import { ReactComponent as uploadSvg } from '../../resources/images/ico-upload.svg';
import { ReactComponent as speedSvg } from '../../resources/images/ico-speed.svg';
import { ReactComponent as agilitySvg } from '../../resources/images/ico-agility.svg';
import { ReactComponent as explosionSvg } from '../../resources/images/ico-explosion.svg';
import { ReactComponent as playSvg } from '../../resources/images/ico-play.svg';
import styles from './PlayerProfilePage.module.scss';
import ShareComponent from './ShareComponent/ShareComponent';

class PlayerProfilePage extends Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;
    formRef = React.createRef<FormInstance>();

    constructor(props: Props) {
        super(props);
        this.state = {};
    }

    componentDidMount() {
        this.init();
    }

    /** METHODS **/

    init = async () => {
        try {
            this.setState({ loading: true });
            const mediaPage = await mediaApi.list(0, tableService.pageSize, 'name', true);

            this.setState({ mediaPage, loading: false });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    list = async (pagination: TablePaginationConfig, filters: any, sorter: any) => {
        const { searchText } = this.state;
        try {
            this.setState({ loading: true });
            const page = pagination.current!;
            const pageSize = pagination.pageSize!;
            const sortField = sorter.field;
            const sortOrder = sorter.order === 'ascend';
            const mediaPage = await mediaApi.list(page - 1, pageSize, sortField, sortOrder, searchText);
            this.setState({ mediaPage, loading: false });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        }
    };

    search = async (searchText: string) => {
        const { mediaPage } = this.state;
        try {
            this.setState({ loading: true });
            const pageSize = mediaPage!.size;
            const sortField = mediaPage!.sort.field!;
            const sortOrder = mediaPage!.sort.order!;
            const mediaPageNew = await mediaApi.list(0, pageSize, sortField, sortOrder, searchText);
            this.setState({ mediaPage: mediaPageNew, searchText, loading: false });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        }
    };

    showModal = (visible: boolean) => {
        this.setState({ modalVisible: visible });
    };

    /*** COMPONENTS ***/

    renderHeader = (desktop: boolean): React.ReactElement | undefined => {
        if (desktop) {
            return (
                <div className={styles.header}>
                    <div className={styles.headerBackground}>
                        <div className={styles.headerBackgroundGradient}></div>
                        <div className={styles.headerBackgroundImage}></div>
                    </div>

                    <div className={styles.buttons}>
                        <Button shape="round">
                            <FormattedMessage id="player.profile.private" />
                        </Button>
                        <Button shape="round">
                            <FormattedMessage id="player.profile.public" />
                        </Button>
                        <Button shape="round" className={styles.upload} icon={<Icon component={uploadSvg} />}>
                            <FormattedMessage id="player.profile.upload" />
                        </Button>
                        <Button
                            shape="round"
                            className={styles.share}
                            icon={<Icon component={shareSvg} />}
                            onClick={() => this.showModal(true)}
                        >
                            <FormattedMessage id="player.profile.share" />
                        </Button>
                    </div>
                </div>
            );
        } else {
            return (
                <>
                    <Button type="ghost" icon={<ShareAltOutlined />} />
                </>
            );
        }
    };

    renderButtons = (desktop: boolean): React.ReactElement | undefined => {
        if (desktop) {
            return (
                <>
                    <div className={styles.records}>
                        <header>
                            <h2>
                                <FormattedMessage id="player.profile.records" />
                            </h2>
                            <Button type={'text'}>
                                <FormattedMessage id="player.profile.viewPlayerStats" />
                            </Button>
                        </header>

                        <div className={styles.recordsData}>
                            <div className={styles.record}>
                                <Statistic
                                    className={styles.speed}
                                    title={
                                        <>
                                            <Icon className={styles.icon} component={speedSvg} />
                                            <h3>
                                                <FormattedMessage id="player.profile.speed" />
                                            </h3>
                                            <p>
                                                <FormattedMessage id="player.profile.10yard" />
                                            </p>
                                        </>
                                    }
                                    value={1.83 + 's'}
                                    precision={2}
                                    suffix={
                                        <div className={styles.label}>
                                            <FormattedMessage id="player.profile.upperstrength" />
                                        </div>
                                    }
                                />
                                <Button
                                    className={styles.play}
                                    shape={'round'}
                                    type="primary"
                                    icon={<Icon component={playSvg} />}
                                >
                                    <FormattedMessage id="player.profile.watch" />
                                </Button>
                            </div>

                            <div className={styles.record}>
                                <Statistic
                                    className={styles.agility}
                                    title={
                                        <>
                                            <Icon className={styles.icon} component={agilitySvg} />
                                            <h3>
                                                <FormattedMessage id="player.profile.agility" />
                                            </h3>
                                            <p>
                                                <FormattedMessage id="player.profile.5105" />
                                            </p>
                                        </>
                                    }
                                    value={5.91 + 's'}
                                    precision={2}
                                    suffix={
                                        <div className={styles.label}>
                                            <FormattedMessage id="player.profile.leghipstrength" />
                                        </div>
                                    }
                                />
                                <Button
                                    className={styles.play}
                                    shape={'round'}
                                    type="primary"
                                    icon={<Icon component={playSvg} />}
                                >
                                    <FormattedMessage id="player.profile.watch" />
                                </Button>
                            </div>

                            <div className={styles.record}>
                                <Statistic
                                    className={styles.explosion}
                                    title={
                                        <>
                                            <Icon className={styles.icon} component={explosionSvg} />
                                            <h3>
                                                <FormattedMessage id="player.profile.explosion" />
                                            </h3>
                                            <p>
                                                <FormattedMessage id="player.profile.10yard" />
                                            </p>
                                        </>
                                    }
                                    value={13.2 + '"'}
                                    precision={2}
                                    suffix={
                                        <div className={styles.label}>
                                            <FormattedMessage id="player.profile.explosivestrength" />
                                        </div>
                                    }
                                />
                                <Button
                                    className={styles.play}
                                    shape={'round'}
                                    type="primary"
                                    icon={<Icon component={playSvg} />}
                                >
                                    <FormattedMessage id="player.profile.watch" />
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className={styles.photos}>
                        <header>
                            <h2>
                                <FormattedMessage id="player.profile.photosVideos" />
                            </h2>
                            <Button type={'text'}>
                                <FormattedMessage id="player.profile.viewAll" />
                            </Button>
                        </header>
                    </div>
                </>
            );
        } else {
            return (
                <>
                    <div className={styles.records}>
                        <header>
                            <h2>
                                <FormattedMessage id="player.profile.records" />
                            </h2>
                        </header>

                        <div className={styles.recordsData}>
                            <div className={styles.record}>
                                <Statistic
                                    className={styles.speed}
                                    title={
                                        <>
                                            <Icon className={styles.icon} component={speedSvg} />
                                            <h3>
                                                <FormattedMessage id="player.profile.speed" />
                                            </h3>
                                            <p>
                                                <FormattedMessage id="player.profile.10yard" />
                                            </p>
                                        </>
                                    }
                                    value={1.83 + 's'}
                                    precision={2}
                                    suffix={
                                        <div className={styles.label}>
                                            <FormattedMessage id="player.profile.upperstrength" />
                                        </div>
                                    }
                                />
                                <Button
                                    className={styles.play}
                                    shape={'round'}
                                    type="primary"
                                    icon={<Icon component={playSvg} />}
                                >
                                    <FormattedMessage id="player.profile.watch" />
                                </Button>
                            </div>

                            <div className={styles.record}>
                                <Statistic
                                    className={styles.agility}
                                    title={
                                        <>
                                            <Icon className={styles.icon} component={agilitySvg} />
                                            <h3>
                                                <FormattedMessage id="player.profile.agility" />
                                            </h3>
                                            <p>
                                                <FormattedMessage id="player.profile.5105" />
                                            </p>
                                        </>
                                    }
                                    value={5.91 + 's'}
                                    precision={2}
                                    suffix={
                                        <div className={styles.label}>
                                            <FormattedMessage id="player.profile.leghipstrength" />
                                        </div>
                                    }
                                />
                                <Button
                                    className={styles.play}
                                    shape={'round'}
                                    type="primary"
                                    icon={<Icon component={playSvg} />}
                                >
                                    <FormattedMessage id="player.profile.watch" />
                                </Button>
                            </div>

                            <div className={styles.record}>
                                <Statistic
                                    className={styles.explosion}
                                    title={
                                        <>
                                            <Icon className={styles.icon} component={explosionSvg} />
                                            <h3>
                                                <FormattedMessage id="player.profile.explosion" />
                                            </h3>
                                            <p>
                                                <FormattedMessage id="player.profile.10yard" />
                                            </p>
                                        </>
                                    }
                                    value={13.2 + '"'}
                                    precision={2}
                                    suffix={
                                        <div className={styles.label}>
                                            <FormattedMessage id="player.profile.explosivestrength" />
                                        </div>
                                    }
                                />
                                <Button
                                    className={styles.play}
                                    shape={'round'}
                                    type="primary"
                                    icon={<Icon component={playSvg} />}
                                >
                                    <FormattedMessage id="player.profile.watch" />
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className={styles.photos}>
                        <header>
                            <h2>
                                <FormattedMessage id="player.profile.photosVideos" />
                            </h2>
                            <Button type={'text'}>
                                <FormattedMessage id="player.profile.viewAll" />
                            </Button>
                        </header>
                    </div>

                    {/*<Row gutter={[28, 0]}>
											<Col span={9}>
													<Select
															placeholder={<FormattedMessage id="player.profile.all" />}
															style={{ width: 80 }}
													></Select>
											</Col>
											<Col span={5}>
													<Button size={'large'} shape={'round'} icon={<ProfileOutlined />}></Button>
											</Col>
											<Col span={5}>
													<Button size={'large'} shape={'round'} icon={<TableOutlined />}></Button>
											</Col>
									</Row>*/}
                </>
            );
        }
    };

    renderContent = (desktop: boolean): React.ReactElement | undefined => {
        const { modalVisible, mediaPage, loading } = this.state;
        const items = mediaPage ? mediaPage.content : [];

        return (
            <>
                <div className={styles.mobileHeaderBackground}>
                    <div className={styles.headerBackgroundGradient}></div>
                    <div className={styles.headerBackgroundImage}></div>
                </div>
                <ShareComponent visible={modalVisible} onCancel={() => this.showModal(false)} />
                <div className={styles.player}>
                    <header>
                        <div className={styles.picture}>
                            <Image
                                src="error"
                                fallback="data:image/png;base64,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"
                            />
                        </div>

                        <div className={styles.playerInfo}>
                            <h1>Carter Butcher</h1>
                            <ul className={styles.stats}>
                                <li className={styles.place}>3rd</li>
                                <li className={styles.height}>4'6"</li>
                                <li className={styles.weight}>75 lbs</li>
                            </ul>
                            <div className={styles.misc}>
                                <p>C, 2BV, SS, P</p>
                            </div>
                        </div>

                        <div className={styles.playerScore}>
                            <div>
                                <div className={styles.score}>497</div>
                                <p>T100 Score</p>
                                <img width="70" src={process.env.PUBLIC_URL + '/img/top100-logo.png'} alt="" />
                            </div>
                        </div>

                        <div className={styles.playerStats}>
                            <ul>
                                <li>
                                    <label>GPA</label>
                                    3.6
                                </li>
                                <li>
                                    <label>SAT</label>
                                    1230
                                </li>
                                <li>
                                    <label>ACT</label>
                                    23
                                </li>
                            </ul>
                        </div>
                    </header>

                    <div className={styles.about}>
                        <h2>
                            <FormattedMessage id="player.profile.about" />
                        </h2>
                        <p>
                            <FormattedMessage id="player.profile.description" />
                        </p>
                        <a href="#more">More</a>
                    </div>

                    {this.renderButtons(desktop)}
                </div>

                <List
                    className={styles.photosList}
                    grid={{
                        gutter: 40,
                    }}
                    dataSource={items}
                    renderItem={(media) => (
                        <List.Item>
                            <Card className={styles.card} cover={<div className={styles.image} />}>
                                <Card.Meta className={styles.meta} title={media.name} />
                            </Card>
                        </List.Item>
                    )}
                    loading={loading}
                />
            </>
        );
    };

    render() {
        const playerId = this.props.match.params.id;
        return (
            <LayoutComponent
                page="player-profile"
                content={this.renderContent}
                header={this.renderHeader}
                playerId={playerId}
            />
        );
    }
}
export default injectIntl(PlayerProfilePage);

type ParamsType = { id: string };
interface Props extends RouteComponentProps<ParamsType>, WrappedComponentProps {}

interface State {
    modalVisible?: boolean;
    mediaPage?: LegacyPage<Media>;
    loading?: boolean;
    searchText?: string;
}
